import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR } from './lib';
import { AlternativePicker, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock } from './SharedBlocks';

const PremiumLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied,
  } = ctx;

  const cust = quote.customerData || {};
  const sans = "'Inter', system-ui, sans-serif";

  const Row = ({ service, color, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '18px 0', borderBottom: `1px solid ${theme.surfaceBorder}`, opacity: isOpt && !on ? 0.5 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {isOpt && (
                <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)}
                  style={{ width: 18, height: 18, accentColor: theme.primary, cursor: 'pointer' }} />
              )}
              <p style={{ fontSize: 18, fontWeight: 500, color: theme.textPrimary, margin: 0, fontFamily: theme.font }}>{service.name}</p>
            </div>
            {service.description && <p style={{ fontSize: 13, color: theme.textSecondary, marginTop: 6, lineHeight: 1.7, fontFamily: sans }}>{service.description}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap', fontFamily: sans }}>
            <span style={{ fontSize: 17, fontWeight: 600, color: theme.textPrimary }}>{formatEUR(lineTotal(service))}</span>
            {suffix && <div style={{ fontSize: 11, color: theme.textMuted }}>{suffix}</div>}
          </div>
        </div>
      </div>
    );
  };

  const Group = ({ title, services, color, suffix }) => {
    if (!services.length) return null;
    return (
      <div style={{ marginTop: 8 }}>
        <h3 style={{ fontSize: 12, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color, fontFamily: sans, marginBottom: 2 }}>{title}</h3>
        {services.map((s, i) => <Row key={s.id || i} service={s} color={color} suffix={suffix} />)}
      </div>
    );
  };

  const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: theme.radius, background: 'transparent', border: `1px solid ${theme.surfaceBorder}`, color: theme.textSecondary, cursor: 'pointer', fontSize: 13, fontFamily: sans };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 64px' }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '18px 20px', maxWidth: 760, margin: '0 auto' }}>
        <button onClick={handleSaveAsPdf} style={btn}><Download className="w-4 h-4" />PDF</button>
        <button onClick={handleCopyLink} style={btn}>{linkCopied ? <Check className="w-4 h-4" style={{ color: '#22c55e' }} /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 28px' }}>

        {/* HERO */}
        <div style={{ padding: '56px 0 40px' }}>
          {company?.logo
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="Logo" style={{ height: 36, marginBottom: 40, objectFit: 'contain' }} />
            : <p style={{ fontSize: 12, letterSpacing: 3, color: theme.textMuted, fontFamily: sans, marginBottom: 40 }}>{(company?.name || '').toUpperCase()}</p>}
          {cust.company && <p style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: theme.textMuted, fontFamily: sans, marginBottom: 18 }}>Für {cust.company}</p>}
          <h1 style={{ fontSize: 40, lineHeight: 1.25, fontWeight: 500, margin: 0, color: theme.textPrimary }}>
            {quote.heroHeadline || quote.quoteTitle || 'Ein Angebot, das überzeugt.'}
          </h1>
          {quote.quoteIntro && <p style={{ fontSize: 16, color: theme.textSecondary, marginTop: 22, lineHeight: 1.8, fontFamily: sans, whiteSpace: 'pre-wrap' }}>{quote.quoteIntro}</p>}
        </div>

        {quote.videoUrl && (
          <div className="no-print" style={{ marginBottom: 40 }}>
            <video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius, maxHeight: 440 }} />
          </div>
        )}

        {quote.customerArguments && (
          <div style={{ borderTop: `1px solid ${theme.surfaceBorder}`, borderBottom: `1px solid ${theme.surfaceBorder}`, padding: '28px 0', margin: '8px 0 24px' }}>
            <div style={{ fontSize: 16, color: theme.textSecondary, lineHeight: 1.8, fontFamily: sans }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}

        <Group title="Einmalige Leistungen" services={einmaligServices} color={theme.einmalig} suffix="" />
        <Group title="Monatliche Leistungen" services={monatlichServices} color={theme.monatlich} suffix="/ Monat" />
        <Group title="Jährliche Leistungen" services={jaehrlichServices} color={theme.jaehrlich} suffix="/ Jahr" />

        {/* Price statement */}
        <div style={{ padding: '44px 0 36px', textAlign: 'center' }}>
          <p style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: theme.textMuted, fontFamily: sans, marginBottom: 10 }}>Investition gesamt</p>
          <p style={{ fontSize: 'clamp(34px, 9vw, 52px)', fontWeight: 500, color: theme.primary, margin: 0, lineHeight: 1 }}>{formatEUR(einmaligTotals.brutto)}</p>
          {(monatlichTotals.brutto > 0 || jaehrlichTotals.brutto > 0) && (
            <p style={{ fontSize: 15, color: theme.textSecondary, marginTop: 12, fontFamily: sans }}>
              {monatlichTotals.brutto > 0 && <>zzgl. {formatEUR(monatlichTotals.brutto)} / Monat </>}
              {jaehrlichTotals.brutto > 0 && <>· {formatEUR(jaehrlichTotals.brutto)} / Jahr</>}
            </p>
          )}
          <p style={{ fontSize: 12, color: theme.textMuted, marginTop: 8, fontFamily: sans }}>inkl. 19% MwSt.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <TermsBlock quote={quote} company={company} theme={theme} />
          <AcceptanceBlock ctx={ctx} theme={theme} />
          <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FeedbackBlock viewHash={viewHash} theme={theme} />
            <QuestionBlock viewHash={viewHash} theme={theme} />
          </div>
          <FooterBlock company={company} theme={theme} />
        </div>
      </div>
    </div>
  );
};

export default PremiumLayout;
